import * as THREE from 'three'
import { TileType, type Layout } from '../../data/maze-data'
import { loadModel } from '../../utils/glb-loader'

const names = ['wall-section-1-0','wall-section-1-1','wall-section-2-0','wall-section-2-1','wall-section-3-0','wall-section-3-1','wall-section-4-0','wall-section-4-1','encrusting-coral-patch','fractured-stone-0','fractured-stone-1','fractured-stone-2','fractured-stone-3','fractured-stone-4','fractured-stone-5','fractured-stone-6','fractured-stone-7','kelp-ribbons','coral-antlers'] as const
export type ReefAsset = typeof names[number]
const geometry = new Map<ReefAsset, THREE.BufferGeometry>()
let pending: Promise<void> | undefined
/** Startup barrier: cached authored geometry is shared for the app lifetime; level builds clone it. */
export function preloadReefAssets(): Promise<void> {
  if (!pending) pending = Promise.all(names.map(async name => {
    const scene = await loadModel(`${import.meta.env.BASE_URL}models/reef/${name}.glb`)
    scene.updateMatrixWorld(true)
    let source: THREE.Mesh | undefined
    scene.traverse(object => { if ((object as THREE.Mesh).isMesh) source = object as THREE.Mesh })
    if(!source) throw new Error(`Reef asset ${name} has no mesh`)
    const baked=source.geometry.clone().applyMatrix4(source.matrixWorld)
    geometry.set(name,baked)
  })).then(()=>{}).catch(error=>{pending=undefined;throw error})
  return pending
}
export function reefGeometry(name: ReefAsset): THREE.BufferGeometry | undefined { return geometry.get(name) }
export function reefAssetsReady(): boolean { return geometry.size===names.length }

export interface ReefWallSection { row: number; col: number; axis: 'x' | 'z'; length: number }
/** Greedy longest-run partition. Every section remains inside the exact wall-cell union. */
export function reefWallSections(layout: Layout): ReefWallSection[] {
  const claimed = new Set<string>(), sections: ReefWallSection[] = []
  const available = (row: number, col: number) => layout[row]?.[col] === TileType.Wall && !claimed.has(`${row},${col}`)
  for(let row=0;row<layout.length;row++) for(let col=0;col<layout[row].length;col++) {
    if(!available(row,col)) continue
    let horizontal=1, vertical=1
    while(available(row,col+horizontal)) horizontal++
    while(available(row+vertical,col)) vertical++
    const axis = horizontal >= vertical ? 'x' : 'z'
    const run = Math.max(horizontal,vertical)
    const length = run===5 ? 3 : Math.min(4,run)
    sections.push({row,col,axis,length})
    for(let i=0;i<length;i++) claimed.add(`${row+(axis==='z'?i:0)},${col+(axis==='x'?i:0)}`)
  }
  return sections
}
